<template>
    <v-dialog
        v-model="dialog"
        max-width="1200px"
        scrollable
        persistent
        @click:outside="close"
    >
        <v-card>
            <v-card-title>
                <v-btn
                    small
                    icon
                    @click="close"
                    color="primary"
                    class="ml-n3 mr-2"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                <h2 class="font-weight-regular">Work Order Alerts</h2>
                <v-spacer />
                <v-chip
                    :color="getStatusColor('pending')"
                    class="mr-2"
                    style="color: white;"
                    small
                >
                    Open: {{ openAlertsCount }}
                </v-chip>
                <v-chip
                    :color="getStatusColor('resolved')"
                    style="color: white;"
                    small
                >
                    Resolved: {{ resolvedAlertsCount }}
                </v-chip>
            </v-card-title>
            <v-divider />
            <v-card-text style="max-height: 70vh;">
                <v-data-table
                    :headers="headers"
                    :items="alerts"
                    :loading="loading"
                    class="elevation-0"
                    :mobile-breakpoint="0"
                    @click:row="viewAlert"
                >
                    <template v-slot:[`item.alertType`]="{ item }">
                        <v-chip
                            :color="getAlertTypeColor(item.alertType)"
                            class="text-capitalize"
                            small
                            dark
                        >
                            {{ item.alertType }}
                        </v-chip>
                    </template>
                    <template v-slot:[`item.classification`]="{ item }">
                        <v-chip
                            :color="
                                item.classification === 'major modification'
                                    ? 'orange'
                                    : 'blue'
                            "
                            class="text-capitalize"
                            small
                            dark
                        >
                            {{ item.classification }}
                        </v-chip>
                    </template>
                    <template v-slot:[`item.status`]="{ item }">
                        <v-chip
                            :color="getStatusColor(item.status)"
                            class="text-capitalize"
                            small
                            dark
                        >
                            {{ item.status }}
                        </v-chip>
                    </template>
                    <template v-slot:[`item.currentStep`]="{ item }">
                        <span
                            v-if="
                                item.currentStep === 'supervisor' ||
                                    item.currentStep === 'supervisor_approval'
                            "
                            class="text-capitalize"
                        >
                            {{ getCoordinatorNames(item) }}
                        </span>
                        <span v-else class="text-capitalize">{{
                            formatStep(item.currentStep)
                        }}</span>
                    </template>
                    <template v-slot:[`item.selectedPartNumbers`]="{ item }">
                        <div
                            v-if="
                                item.selectedPartNumbers &&
                                    item.selectedPartNumbers.length > 0
                            "
                        >
                            <v-chip
                                v-for="(pn, index) in sortPartNumbers(
                                    item.selectedPartNumbers
                                ).slice(0, 3)"
                                :key="pn.id || pn.partNumber + String(index)"
                                small
                                class="mr-1 mb-1"
                            >
                                {{ pn.partNumber }}
                            </v-chip>
                            <span
                                v-if="item.selectedPartNumbers.length > 3"
                                class="caption"
                            >
                                +{{ item.selectedPartNumbers.length - 3 }} more
                            </span>
                        </div>
                        <span v-else class="grey--text">-</span>
                    </template>
                    <template v-slot:[`item.createdOn`]="{ item }">
                        {{
                            item.createdOn
                                ? formatDate(
                                      item.createdOn.seconds ||
                                          item.createdOn._seconds
                                  )
                                : '-'
                        }}
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                        <v-menu offset-y :close-on-content-click="false">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    small
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                    :loading="isOpeningMakeChanges(item)"
                                    :disabled="
                                        !hasActionOptions(item) ||
                                            makeChangesOpening
                                    "
                                    @click.stop
                                >
                                    <v-icon small>mdi-dots-vertical</v-icon>
                                </v-btn>
                            </template>
                            <v-list>
                                <v-list-item
                                    v-if="canEditAlert(item)"
                                    :disabled="makeChangesOpening"
                                    @click.stop="editAlert(item)"
                                >
                                    <v-list-item-title>Edit</v-list-item-title>
                                </v-list-item>
                                <v-list-item
                                    v-if="canResolveAlert(item)"
                                    :disabled="makeChangesOpening"
                                    @click.stop="openResolveDialog(item)"
                                >
                                    <v-list-item-title
                                        >Review Alert</v-list-item-title
                                    >
                                </v-list-item>
                                <v-list-item
                                    v-if="canMakeChanges(item)"
                                    :disabled="
                                        makeChangesOpening &&
                                            !isOpeningMakeChanges(item)
                                    "
                                    @click.stop="openMakeChangesDialog(item)"
                                >
                                    <v-list-item-title
                                        >Make Changes</v-list-item-title
                                    >
                                    <v-list-item-action
                                        v-if="isOpeningMakeChanges(item)"
                                        class="my-0 ml-2"
                                    >
                                        <v-progress-circular
                                            indeterminate
                                            size="16"
                                            width="2"
                                        />
                                    </v-list-item-action>
                                </v-list-item>
                                <v-list-item
                                    v-if="canApproveAlert(item)"
                                    @click.stop="openApproveDialog(item)"
                                >
                                    <v-list-item-title
                                        >Approve</v-list-item-title
                                    >
                                </v-list-item>
                                <v-list-item
                                    v-if="
                                        canDeleteNonExecutableAlert(item) &&
                                            !canDeleteAlert(item)
                                    "
                                    @click.stop="
                                        openDeleteNonExecutableDialog(item)
                                    "
                                >
                                    <v-list-item-title class="red--text"
                                        >Delete Alert
                                    </v-list-item-title>
                                </v-list-item>
                                <v-list-item
                                    v-if="
                                        !canDeleteNonExecutableAlert(item) &&
                                            canDeleteAlert(item)
                                    "
                                    @click.stop="openDeleteAlertDialog(item)"
                                >
                                    <v-list-item-title class="red--text"
                                        >Delete Alert
                                    </v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>
                </v-data-table>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-btn
                    v-if="canCreateAlert"
                    color="primary"
                    rounded
                    @click="createAlert"
                >
                    <v-icon small class="mr-1">mdi-plus</v-icon>
                    CREATE ALERT
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import moment from 'moment'
import alertActionsMixin from '@/mixins/alertActionsMixin'

export default {
    name: 'AlertsList',
    mixins: [alertActionsMixin],
    props: {
        workOrder: {
            type: Object,
            required: true,
        },
        value: {
            type: Boolean,
            default: false,
        },
        readOnlyWO: {
            type: Boolean,
            default: false,
        },
        makeChangesOpening: {
            type: Boolean,
            default: false,
        },
        openingMakeChangesAlertId: {
            type: String,
            default: null,
        },
    },
    data: () => ({
        loading: false,
        alerts: [],
        companyId: JSON.parse(localStorage.getItem('company')),
        currentUserId: JSON.parse(localStorage.getItem('userId')),
        settingProcesses: null,
        users: {},
        headers: [
            {
                text: 'Type',
                value: 'alertType',
                sortable: true,
            },
            {
                text: 'Classification',
                value: 'classification',
                sortable: true,
            },
            {
                text: 'Status',
                value: 'status',
                sortable: true,
            },
            {
                text: 'Current Step',
                value: 'currentStep',
                sortable: true,
            },
            {
                text: 'Part Numbers',
                value: 'selectedPartNumbers',
                sortable: false,
            },
            {
                text: 'Created On',
                value: 'createdOn',
                sortable: true,
            },
            {
                text: 'Actions',
                value: 'actions',
                sortable: false,
                align: 'center',
            },
        ],
    }),
    computed: {
        dialog: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
        openAlertsCount() {
            return this.alerts.filter(a => a.status !== 'resolved').length
        },
        resolvedAlertsCount() {
            return this.alerts.filter(a => a.status === 'resolved').length
        },
        canCreateAlert() {
            // Only operators can create alerts on approved work orders
            return this.workOrder.status === 'approved'
        },
    },
    watch: {
        dialog(newVal) {
            if (newVal) {
                this.loadAlerts()
            }
        },
    },
    async mounted() {
        await Promise.all([
            this.loadUsers(),
            this.loadSettings(this.companyId),
            this.loadCanApproveAlerts(),
        ])
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        async loadUsers() {
            try {
                const {
                    data: { users },
                } = await API.getLiteUsers()
                this.users = {}
                if (Array.isArray(users)) {
                    users.forEach(user => {
                        this.users[user.id] = user
                    })
                } else if (typeof users === 'object') {
                    this.users = users
                }
            } catch (error) {
                console.error('Error loading users:', error)
            }
        },
        sortPartNumbers(partNumbers) {
            if (!partNumbers || !partNumbers.length) return []
            return [...partNumbers].sort((a, b) => {
                const last4A = parseInt(String(a.partNumber).slice(-4), 10)
                const last4B = parseInt(String(b.partNumber).slice(-4), 10)
                return last4A - last4B
            })
        },
        async loadAlerts() {
            try {
                this.loading = true
                const alerts = await API.getAlertsByWorkOrder(this.workOrder.id)
                // Enrich alerts the same way AlertsDashboard.vue does, so the
                // shared alertActionsMixin/dialogs can rely on `alert.workOrder`
                // and `alert.alertWOIsClosed` regardless of the entry point.
                this.alerts = (alerts || []).map(alert => ({
                    ...alert,
                    workOrder: this.workOrder,
                    alertWOIsClosed:
                        this.workOrder.status === 'closed' || this.readOnlyWO,
                }))
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        /** Allows the parent (WOGeneralInfo.vue) to silently refresh the list after an action. */
        reload() {
            this.loadAlerts()
        },
        formatDate(timestamp) {
            if (!timestamp) return ''
            return moment.unix(timestamp).format('MM/DD/YYYY HH:mm')
        },
        async loadSettings(companyId) {
            try {
                const {
                    data: { settings },
                } = await API.getSettings(companyId ? { companyId } : {})
                this.settingProcesses = settings.find(
                    s => s.name === 'Processes'
                )
            } catch (error) {
                console.error('Error loading settings:', error)
            }
        },
        getCoordinatorNames(alert) {
            if (!alert || !alert.processId || !this.settingProcesses) {
                return 'Supervisor'
            }
            const processes = this.settingProcesses.processes || []
            const process = processes.find(p => p.id === alert.processId)
            if (
                !process ||
                !process.coordinators ||
                process.coordinators.length === 0
            ) {
                return 'Supervisor'
            }
            const names = process.coordinators
                .map(id => (this.users[id] && this.users[id].name) || null)
                .filter(Boolean)
            return names.length > 0 ? names.join(', ') : 'Supervisor'
        },
        createAlert() {
            this.$emit('create-alert')
        },
        close() {
            this.$emit('input', false)
        },
        // Overrides of alertActionsMixin's dialog-opening methods: this
        // component only lists alerts, the actual dialogs live in the parent
        // (WOGeneralInfo.vue / AlertsDashboard.vue equivalent), so every
        // action is delegated upwards via events instead of local state.
        viewAlert(alert) {
            this.$emit('view-alert', alert)
        },
        editAlert(alert) {
            this.$emit('edit-alert', alert)
        },
        openResolveDialog(alert) {
            this.$emit('resolve-alert', alert)
        },
        openMakeChangesDialog(alert) {
            this.$emit('make-changes-alert', alert)
        },
        isOpeningMakeChanges(alert) {
            if (!this.makeChangesOpening || !alert) return false
            const key = `${alert.workOrderId || ''}:${alert.id || ''}`
            return this.openingMakeChangesAlertId === key
        },
        openApproveDialog(alert) {
            this.$emit('approve-alert', alert)
        },
        openDeleteAlertDialog(alert) {
            this.$emit('delete-alert', alert)
        },
        openDeleteNonExecutableDialog(alert) {
            this.$emit('delete-non-executable-alert', alert)
        },
    },
}
</script>

<style scoped></style>
